/**
 * The panel that opens under a value when the value, or its Choose value / Review conflict button, is
 * clicked: every value the sources gave for the field, where each came from, and a box to type a
 * different one.
 *
 * Expects the value and its catalog entry, the element the panel hangs under (the value's button,
 * so a click on it is not a click outside), and the callbacks that decide the value, close the
 * panel and open a source document.
 *
 * Renders, in order: the sentence pickerHint gives; a "Current" option holding the approved value,
 * where there is one; one option per waiting group, newest first, each with one line per source
 * and, under them, the reason any source's value did not pass a check; the option "Enter a
 * different value" with the input the field's type takes (a select of the field's choices, yes /
 * no or the months, from selectOptions below; a month select and a day select for a month
 * and day, which is stored as MM-DD; a date box; a box for a taxpayer number's nine digits, which
 * shows the digits only while it has the focus and dots otherwise; or a text box); and Cancel beside
 * a green button that reads "Keep current value" while Current is selected and "Accept value"
 * otherwise.
 *
 * The newest waiting group is selected when the panel opens; where nothing waits, Current is, and
 * on an empty value the box to type in is. The green button sends:
 *  - a waiting group: { kind: 'accept', rowId } with the id of that group's first source;
 *  - Current on a changed value: { kind: 'keep' };
 *  - Current on an approved value with nothing waiting: nothing, the panel just closes;
 *  - the typed text: { kind: 'type', value } in the form the column stores (storedValueFromInput).
 * It is disabled while the typed option is selected and the box is empty, or, for a month and day,
 * while either select has nothing picked.
 *
 * In the owners, officers, subsidiaries and state registrations tables the panel is opened with
 * three more props: `acceptLabel` replaces "Accept value" on the green button ("Use this value"),
 * `note` is a line under the first sentence (ROW_PICKER_NOTE), and `initialDecision` is the choice
 * already made for the cell, which is selected when the panel opens instead of the newest waiting
 * group (a typed value opens with the box filled in). On a document's summary screen (ReviewModal)
 * `thisDocumentId` names the document reviewed, and every option one of whose sources is that
 * document carries the tag "This document" beside its value.
 *
 * Focus goes to the selected option when the panel opens. Escape and Cancel close it and put the
 * focus back on the value; a mouse press outside the panel and outside the value closes it and
 * leaves the focus where that press put it. After it is drawn, the panel is moved left by as much
 * as its right edge passes the window less a 16-pixel margin, never past that margin on the left,
 * so it does not make the page scroll sideways. Inside an ancestor that scrolls sideways (the
 * modal-box of the person window in ListTable), that ancestor's inside edges are used instead of
 * the window's, and the panel is made narrower when it is wider than the room between them.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type {
  TaxProfileFieldDefinition,
  TaxProfileListColumnDefinition,
} from '@shared/constants/taxProfileFields';
import type {
  TaxProfileFieldValue,
  TaxProfileValueSource,
} from '@shared/types/taxProfile';
import {
  MONTH_NAMES,
  NOT_ON_FILE,
  daysInMonth,
  editorTextFromStored,
  formatFieldValue,
  monthDayOf,
  monthDayText,
  storedValueFromInput,
} from '@/pages/TaxProfile/format';
import {
  VALUE_WORDS,
  checkFailedSentence,
  currentValueLine,
  pickerHint,
  sourceLine,
} from '@/pages/TaxProfile/wording';
import type { SourcePiece } from '@/pages/TaxProfile/wording';
import SourceText from '@/pages/TaxProfile/components/SourceText';

/**
 * The months a month field offers, with the number each one is stored as. The month and day
 * selects of a month-and-day field offer the same months.
 */
const MONTH_OPTIONS = MONTH_NAMES.map((label, index) => ({
  value: String(index + 1),
  label,
}));

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/**
 * The options of the select a field of this type gets under "Enter a different value", and null
 * where it gets a box to type in.
 */
const selectOptions = (
  definition: Pick<
    TaxProfileFieldDefinition,
    'type' | 'choices' | 'booleanLabels'
  >
) => {
  if (definition.type === 'choice') {
    return (definition.choices ?? []).map((choice) => ({
      value: choice.value,
      label: choice.label,
    }));
  }
  if (definition.type === 'boolean') {
    const labels = definition.booleanLabels;
    return labels
      ? [
          { value: 'true', label: labels.true },
          { value: 'false', label: labels.false },
        ]
      : YES_NO_OPTIONS;
  }
  if (definition.type === 'month') return MONTH_OPTIONS;
  return null;
};

/** What a person decided in the panel, or with a line's Accept button. */
export type FieldDecision =
  | { kind: 'accept'; rowId: number }
  | { kind: 'keep' }
  | { kind: 'type'; value: string | number | boolean | null };

/** The panel hangs under the value, at most 35rem wide and never wider than the window less 2rem. */
const PANEL_CLASS =
  'absolute left-0 top-full mt-1 z-30 w-[35rem] max-w-[calc(100vw-2rem)] rounded-xl border ' +
  'border-base-300 bg-base-100 shadow-xl p-3 flex flex-col gap-2 text-sm font-normal whitespace-normal';

/** The space kept between the panel and the window's edges, in pixels. */
const WINDOW_MARGIN = 16;

/**
 * The left and right edges, in window pixels, the panel has to stay inside: the window less
 * WINDOW_MARGIN on each side, narrowed to the inside of the nearest ancestor that scrolls or cuts
 * off its content sideways (a GenericModal's modal-box scrolls, so a panel opened in the person
 * window stays inside that window instead of being cut off by its edge).
 */
const visibleArea = (panel: HTMLElement) => {
  let left = WINDOW_MARGIN;
  let right = window.innerWidth - WINDOW_MARGIN;
  for (let el = panel.parentElement; el; el = el.parentElement) {
    const { overflowX } = window.getComputedStyle(el);
    if (['auto', 'scroll', 'hidden', 'clip'].includes(overflowX)) {
      const box = el.getBoundingClientRect();
      const inner = box.left + el.clientLeft;
      left = Math.max(left, inner + WINDOW_MARGIN);
      right = Math.min(right, inner + el.clientWidth - WINDOW_MARGIN);
      break;
    }
  }
  return { left, right };
};

/**
 * What the panel reads off a value's description: a field of the catalog or a column of a list
 * table (TaxProfileListColumnDefinition), which both carry these.
 */
export type PickerDefinition = Pick<
  TaxProfileFieldDefinition | TaxProfileListColumnDefinition,
  'key' | 'label' | 'type' | 'choices' | 'booleanLabels'
>;

export interface ValuePickerProps {
  field: TaxProfileFieldValue;
  definition: PickerDefinition;
  /** The value's button, which the panel hangs under. */
  anchorRef: RefObject<HTMLElement>;
  onDecide: (decision: FieldDecision) => void;
  /** Closes the panel; `returnFocus` is true for Escape and Cancel. */
  onClose: (returnFocus: boolean) => void;
  onOpenSourceDocument?: (documentId: number) => void;
  busy?: boolean;
  /** The green button's words for every option but Current; "Accept value" when left out. */
  acceptLabel?: string;
  /** A line under the panel's first sentence. */
  note?: string;
  /** The choice already made for this value, which the panel opens on. */
  initialDecision?: FieldDecision | null;
  /** The document whose options carry the tag "This document"; none when left out. */
  thisDocumentId?: number | null;
}

type PickerOption = {
  key: string;
  value: string | number | boolean | null;
  badge: string | null;
  /** True when one of the option's sources is the document `thisDocumentId` names. */
  fromThisDocument: boolean;
  lines: SourcePiece[][];
  reasons: string[];
};

/**
 * The month and the day picked in the two selects of a month-and-day field, each the number as
 * text, or '' while nothing is picked in that select.
 */
type TypedMonthDay = { month: string; day: string };

/** What the two selects open on: the month and day of the value, or nothing picked. */
const typedMonthDayOf = (
  value: string | number | boolean | null | undefined
): TypedMonthDay => {
  const parts = typeof value === 'string' ? monthDayOf(value) : null;
  return parts
    ? { month: String(parts.month), day: String(parts.day) }
    : { month: '', day: '' };
};

const TYPED = 'typed';
const CURRENT = 'current';
const groupKey = (index: number) => `group-${index}`;

/**
 * The option the panel opens with: the one `initialDecision` names, else the newest waiting group,
 * else Current, else the box.
 */
const firstSelection = (
  field: TaxProfileFieldValue,
  initialDecision: FieldDecision | null = null
): string => {
  if (initialDecision?.kind === 'type') return TYPED;
  if (initialDecision?.kind === 'keep' && field.approved) return CURRENT;
  if (initialDecision?.kind === 'accept') {
    const index = field.waiting.findIndex((group) =>
      group.sources.some((source) => source.rowId === initialDecision.rowId)
    );
    if (index >= 0) return groupKey(index);
  }
  if (field.waiting.length > 0) return groupKey(0);
  if (field.approved) return CURRENT;
  return TYPED;
};

export default function ValuePicker({
  field,
  definition,
  anchorRef,
  onDecide,
  onClose,
  onOpenSourceDocument,
  busy = false,
  acceptLabel = VALUE_WORDS.acceptValue,
  note,
  initialDecision = null,
  thisDocumentId = null,
}: ValuePickerProps) {
  const [selected, setSelected] = useState(() =>
    firstSelection(field, initialDecision)
  );
  const [typedText, setTypedText] = useState(() =>
    initialDecision?.kind === 'type'
      ? editorTextFromStored(initialDecision.value, definition)
      : ''
  );
  const [typedMonthDay, setTypedMonthDay] = useState(() =>
    typedMonthDayOf(
      initialDecision?.kind === 'type' ? initialDecision.value : null
    )
  );
  const [offset, setOffset] = useState(0);
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);
  /** Whether the taxpayer number box has the focus, the only time it shows the digits typed. */
  const [taxpayerBoxFocused, setTaxpayerBoxFocused] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const radioRefs = useRef(new Map<string, HTMLInputElement>());
  const typedInputRef = useRef<HTMLInputElement & HTMLSelectElement>(null);

  const { approved } = field;
  const isThisDocument = (source: TaxProfileValueSource) =>
    thisDocumentId != null &&
    source.kind === 'document' &&
    source.documentId === thisDocumentId;
  const options: PickerOption[] = [
    ...(approved
      ? [
          {
            key: CURRENT,
            value: approved.value,
            badge: VALUE_WORDS.current,
            fromThisDocument: isThisDocument(approved),
            lines: [currentValueLine(field, definition.key)],
            reasons: [],
          },
        ]
      : []),
    ...field.waiting.map((group, index) => ({
      key: groupKey(index),
      value: group.value,
      // "Newest" says which of several values is preselected, so a lone proposed value has none.
      badge:
        index === 0 && (field.status === 'changed' || field.waiting.length > 1)
          ? VALUE_WORDS.newest
          : null,
      fromThisDocument: group.sources.some(isThisDocument),
      lines: group.sources.map((source) => sourceLine(source, definition.key)),
      reasons: [
        ...new Set(
          group.sources
            .map((source) => source.checkFailedReason)
            .filter((reason): reason is string => Boolean(reason))
        ),
      ],
    })),
  ];

  const typedChoices = selectOptions(definition);
  const isMonthDay = definition.type === 'month_day';
  // A month and day is sent as MM-DD once both selects have something picked, and counts as empty
  // until then.
  let typedValue = typedText;
  if (isMonthDay) {
    typedValue =
      typedMonthDay.month && typedMonthDay.day
        ? monthDayText(Number(typedMonthDay.month), Number(typedMonthDay.day))
        : '';
  }
  const typedIsEmpty = typedValue.trim() === '';
  // A typed taxpayer number is taken with all nine digits only. The api refuses anything shorter
  // (columnWriteOfTypedTaxpayerNumber in api/src/taxProfile/dashboard/fieldWrites.ts), and a
  // document's four printed digits are a candidate, never something a person types.
  const typedTaxpayerNumberIsShort =
    definition.type === 'tin' &&
    !typedIsEmpty &&
    typedValue.replace(/\D/g, '').length !== 9;
  const typedCannotBeUsed = typedIsEmpty || typedTaxpayerNumberIsShort;
  const dayCount = typedMonthDay.month
    ? daysInMonth(Number(typedMonthDay.month))
    : 31;
  const radioName = `tax-profile-choice-${definition.key}`;

  useEffect(() => {
    const first = firstSelection(field, initialDecision);
    if (first === TYPED) typedInputRef.current?.focus();
    else radioRefs.current.get(first)?.focus();
    // Only on open: the focus is moved once, not on every new answer from the api.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const closeOnOutsidePress = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose(false);
    };
    document.addEventListener('mousedown', closeOnOutsidePress);
    return () => document.removeEventListener('mousedown', closeOnOutsidePress);
  }, [anchorRef, onClose]);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const box = panel.getBoundingClientRect();
    const { left, right } = visibleArea(panel);
    const room = right - left;
    const width = Math.min(box.width, room);
    if (width < box.width) setMaxWidth(room);
    const overflow = box.left + width - right;
    if (overflow <= 0) return;
    setOffset(-Math.max(0, Math.min(overflow, box.left - left)));
  }, []);

  const accept = () => {
    if (selected === TYPED) {
      if (typedCannotBeUsed) return;
      onDecide({
        kind: 'type',
        value: storedValueFromInput(typedValue, definition),
      });
      return;
    }
    if (selected === CURRENT) {
      if (field.status === 'changed') onDecide({ kind: 'keep' });
      else onClose(true);
      return;
    }
    const index = Number(selected.slice('group-'.length));
    const rowId = field.waiting[index]?.sources[0]?.rowId;
    if (rowId != null) onDecide({ kind: 'accept', rowId });
  };

  /**
   * The input "Enter a different value" holds: the month and day selects, a select of the field's
   * options (selectOptions), or a box to type in.
   */
  const typedInput = () => {
    if (isMonthDay) {
      return (
        <span className='flex gap-2'>
          <select
            ref={typedInputRef}
            className='select select-bordered select-sm flex-grow'
            aria-label={`${definition.label} month`}
            value={typedMonthDay.month}
            onFocus={() => setSelected(TYPED)}
            onChange={(event) => {
              const month = event.target.value;
              // A day the new month does not have (the 31st, picking April) is cleared.
              setTypedMonthDay((picked) => ({
                month,
                day:
                  month && Number(picked.day) > daysInMonth(Number(month))
                    ? ''
                    : picked.day,
              }));
              setSelected(TYPED);
            }}
          >
            <option value=''>Month</option>
            {MONTH_OPTIONS.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <select
            className='select select-bordered select-sm w-24'
            aria-label={`${definition.label} day`}
            value={typedMonthDay.day}
            onFocus={() => setSelected(TYPED)}
            onChange={(event) => {
              const day = event.target.value;
              setTypedMonthDay((picked) => ({ ...picked, day }));
              setSelected(TYPED);
            }}
          >
            <option value=''>Day</option>
            {Array.from({ length: dayCount }, (_, index) => (
              <option key={index + 1} value={String(index + 1)}>
                {index + 1}
              </option>
            ))}
          </select>
        </span>
      );
    }
    if (typedChoices) {
      return (
        <select
          ref={typedInputRef}
          className='select select-bordered select-sm w-full'
          aria-label={definition.label}
          value={typedText}
          onFocus={() => setSelected(TYPED)}
          onChange={(event) => {
            setTypedText(event.target.value);
            setSelected(TYPED);
          }}
        >
          <option value=''>{NOT_ON_FILE}</option>
          {typedChoices.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      );
    }
    if (definition.type === 'tin') {
      // A taxpayer number: nine digits, shown only while the box has the focus so they are not left
      // on screen. autoComplete off, since the password type would otherwise offer saved logins.
      return (
        <input
          ref={typedInputRef}
          type={taxpayerBoxFocused ? 'text' : 'password'}
          inputMode='numeric'
          autoComplete='off'
          maxLength={11}
          placeholder={VALUE_WORDS.taxpayerNumberPlaceholder}
          className='input input-bordered input-sm w-full'
          aria-label={definition.label}
          value={typedText}
          onFocus={() => {
            setTaxpayerBoxFocused(true);
            setSelected(TYPED);
          }}
          onBlur={() => setTaxpayerBoxFocused(false)}
          onChange={(event) => {
            setTypedText(event.target.value);
            setSelected(TYPED);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            accept();
          }}
        />
      );
    }
    return (
      <input
        ref={typedInputRef}
        type={definition.type === 'date' ? 'date' : 'text'}
        className='input input-bordered input-sm w-full'
        aria-label={definition.label}
        value={typedText}
        onFocus={() => setSelected(TYPED)}
        onChange={(event) => {
          setTypedText(event.target.value);
          setSelected(TYPED);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          accept();
        }}
      />
    );
  };

  const optionClass = (on: boolean) =>
    `flex gap-3 items-start w-full rounded-lg border p-3 cursor-pointer text-left ${
      on ? 'border-accent bg-accent/10' : 'border-base-300'
    }`;

  return (
    // The panel is a group of radio buttons with two buttons under it; a click anywhere on an
    // option selects it, and the keyboard reaches the same choice through the option's radio.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={panelRef}
      role='dialog'
      aria-label={`Choose ${definition.label}`}
      className={PANEL_CLASS}
      style={{ left: offset, maxWidth }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onClose(true);
      }}
    >
      <p className='text-xs opacity-80'>{pickerHint(field)}</p>
      {note && <p className='text-xs opacity-80'>{note}</p>}
      {options.map((option) => {
        const on = selected === option.key;
        const valueText = formatFieldValue(option.value, definition);
        return (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            key={option.key}
            className={optionClass(on)}
            onClick={() => setSelected(option.key)}
          >
            <input
              ref={(node) => {
                if (node) radioRefs.current.set(option.key, node);
                else radioRefs.current.delete(option.key);
              }}
              type='radio'
              className='radio radio-accent radio-sm mt-0.5'
              name={radioName}
              checked={on}
              onChange={() => setSelected(option.key)}
              aria-label={
                option.key === CURRENT
                  ? `${VALUE_WORDS.current}: ${valueText}`
                  : valueText
              }
            />
            <div className='flex flex-col gap-1 min-w-0'>
              <span className='flex flex-wrap items-center gap-2'>
                <span className='font-semibold break-words'>{valueText}</span>
                {option.badge && (
                  <span className='badge badge-accent badge-outline badge-sm'>
                    {option.badge}
                  </span>
                )}
                {option.fromThisDocument && (
                  <span className='badge badge-ghost badge-sm'>
                    {VALUE_WORDS.thisDocument}
                  </span>
                )}
              </span>
              {option.lines.map((pieces, index) => (
                <SourceText
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  pieces={pieces}
                  onOpenDocument={onOpenSourceDocument}
                  className='text-xs opacity-80'
                />
              ))}
              {option.reasons.map((reason) => (
                <span key={reason} className='text-xs text-warning'>
                  {checkFailedSentence(reason)}
                </span>
              ))}
            </div>
          </div>
        );
      })}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={`${optionClass(selected === TYPED)} items-center`}
        onClick={() => setSelected(TYPED)}
      >
        <input
          type='radio'
          className='radio radio-accent radio-sm'
          name={radioName}
          checked={selected === TYPED}
          onChange={() => setSelected(TYPED)}
          aria-label={
            options.length === 0
              ? VALUE_WORDS.enterValue
              : VALUE_WORDS.enterDifferent
          }
          ref={(node) => {
            if (node) radioRefs.current.set(TYPED, node);
            else radioRefs.current.delete(TYPED);
          }}
        />
        <div className='flex flex-col gap-1 flex-grow text-xs'>
          <span className='opacity-80'>
            {options.length === 0
              ? VALUE_WORDS.enterValue
              : VALUE_WORDS.enterDifferent}
          </span>
          {typedInput()}
          {typedTaxpayerNumberIsShort && (
            <span className='text-warning'>
              {VALUE_WORDS.taxpayerNumberNeedsNine}
            </span>
          )}
        </div>
      </div>
      <div className='flex justify-end gap-2 pt-1'>
        <button
          type='button'
          className='btn btn-sm btn-ghost'
          onClick={() => onClose(true)}
        >
          {VALUE_WORDS.cancel}
        </button>
        <button
          type='button'
          className='btn btn-sm btn-accent'
          disabled={busy || (selected === TYPED && typedCannotBeUsed)}
          onClick={accept}
        >
          {selected === CURRENT ? VALUE_WORDS.keepCurrent : acceptLabel}
        </button>
      </div>
    </div>
  );
}
